/**
 * @copyright Copyright (c) Open-Xchange GmbH, Germany <info@open-xchange.com>
 * @license AGPL-3.0
 *
 * This code is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with OX App Suite. If not, see <https://www.gnu.org/licenses/agpl-3.0.txt>.
 *
 * Any use of the work other than as authorized under this license or copyright law is prohibited.
 */

import { ref, shallowRef, computed } from 'vue'
import { useSignatureDesignerStore } from '@/io.ox/mail/settings/signatures/signature-designer/store/signature-designer-store'
import { storeToRefs } from 'pinia'
import ext from '@/io.ox/core/extensions'
import manifests from '@/io.ox/core/manifests'
import gt from 'gettext'

export function useSignatureWizard () {
  const currentIndex = ref(0)
  const pages = shallowRef([])

  const store = useSignatureDesignerStore()
  const { availableFields } = storeToRefs(store)

  manifests.manager.loadPluginsFor('signature-designer').then(() => {
    pages.value = ext.point('io.ox/signature-designer').list()
  })

  const sidebarSteps = computed(() => [
    {
      id: 'templates',
      sidebarLabel: gt('Templates'),
      icon: 'bi/grid.svg'
    },
    {
      id: 'text',
      sidebarLabel: gt('Text'),
      icon: 'bi/pencil.svg',
      disabled: !availableFields.value.some(field => store.textFields.includes(field))
    },
    {
      id: 'links',
      sidebarLabel: gt('Links'),
      icon: 'bi/link.svg',
      disabled: !availableFields.value.some(field => store.socialFields.includes(field))
    },
    {
      id: 'images',
      sidebarLabel: gt('Images'),
      icon: 'bi/image.svg',
      disabled: !availableFields.value.some(field => store.imageFields.includes(field))
    },
    {
      id: 'theme',
      sidebarLabel: gt('Theme'),
      icon: 'bi/palette.svg',
      disabled: !availableFields.value.some(field => store.themeFields.includes(field))
    }
  ])

  function setPageById (id) {
    const index = pages.value.findIndex(page => page.id === id)
    if (index !== -1) currentIndex.value = index
  }

  function nextStep () {
    let i = currentIndex.value + 1
    while (sidebarSteps.value[i]?.disabled) i++
    currentIndex.value = i
  }

  function previousStep () {
    let i = currentIndex.value - 1
    while (sidebarSteps.value[i]?.disabled) i--
    currentIndex.value = i
  }

  return {
    currentIndex,
    pages,
    sidebarSteps,
    setPageById,
    nextStep,
    previousStep
  }
}
